import { point } from '../../../elements/point';
import { ICON_DEFAULT_FOREGROUND_COLOR } from '../../../graphConstants';
import { SvgPathBuilder } from '../../../svg/SvgPathBuilder';
import { makeTag } from '../../../svg/utils';
import {
  type NodeIconOptions,
  type NodeTypeIcon,
  nodeIconDefaultOptions,
} from '../NodeTypeIcon';
import {
  createRectangleNodeTypeIcon,
  getArrowSvgPath,
  wrapNodeIconInSvgTag,
} from '../common';
import { drawRectangleBackground } from '../drawIconBackground';

const ICON_WIDTH = 40;
const ICON_HEIGHT = 60;

const TOP_GLYPH_X1 = 8;
const TOP_GLYPH_X2 = 32;
const TOP_GLYPH_Y1 = 5.2;
const TOP_GLYPH_Y2 = 16;

const WING_CONTROL_DIST = 14;

function createNx7000Icon(options: NodeIconOptions = {}): string {
  const opts = { ...nodeIconDefaultOptions, ...options };

  const draw7kTopRectangle = (x: number, y: number) =>
    makeTag('rect', {
      x: x - 4.6,
      y: y - 2.6,
      width: 9.2,
      height: 5.2,
      fill: ICON_DEFAULT_FOREGROUND_COLOR,
    });

  const draw7kLine = (x1: number, y1: number, x2: number, y2: number) =>
    makeTag('line', {
      x1,
      y1,
      x2,
      y2,
      stroke: ICON_DEFAULT_FOREGROUND_COLOR,
      'stroke-width': 1.2,
    });

  const draw7kArrow = (toX: number, toY: number) =>
    makeTag('path', {
      d: getArrowSvgPath({
        from: point.create(20, 40),
        to: point.create(toX, toY),
        headWidth: 7.2,
        headHeight: 3.6,
        tailWidth: 2.4,
      }),
      fill: ICON_DEFAULT_FOREGROUND_COLOR,
    });

  const drawWing = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    cdx: number,
    cdy: number,
  ) =>
    makeTag('path', {
      d: new SvgPathBuilder()
        .moveTo(x1, y1)
        .cubicBezierTo(x1 + cdx, y1 + cdy)(x2 + cdx, y2 + cdy)(x2, y2)
        .build(),
      fill: 'none',
      stroke: ICON_DEFAULT_FOREGROUND_COLOR,
      'stroke-width': 0.8,
    });

  const icon = `
    ${drawRectangleBackground(ICON_WIDTH, ICON_HEIGHT, opts.style)}
    
    ${draw7kTopRectangle(TOP_GLYPH_X1, TOP_GLYPH_Y1)}
    ${draw7kTopRectangle(TOP_GLYPH_X1, TOP_GLYPH_Y2)}
    ${draw7kTopRectangle(TOP_GLYPH_X2, TOP_GLYPH_Y1)}
    ${draw7kTopRectangle(TOP_GLYPH_X2, TOP_GLYPH_Y2)}
    ${draw7kLine(TOP_GLYPH_X1, TOP_GLYPH_Y1, TOP_GLYPH_X2, TOP_GLYPH_Y1)}
    ${draw7kLine(TOP_GLYPH_X1, TOP_GLYPH_Y2, TOP_GLYPH_X2, TOP_GLYPH_Y2)}
    ${draw7kLine(TOP_GLYPH_X1, TOP_GLYPH_Y1, TOP_GLYPH_X2, TOP_GLYPH_Y2)}
    ${draw7kLine(TOP_GLYPH_X1, TOP_GLYPH_Y2, TOP_GLYPH_X2, TOP_GLYPH_Y1)}
    
    ${makeTag('circle', {
      cx: 20,
      cy: 40,
      fill: ICON_DEFAULT_FOREGROUND_COLOR,
      r: 6.4,
    })}
    ${draw7kArrow(20, 22)}
    ${draw7kArrow(20, 58)}
    ${draw7kArrow(2, 40)}
    ${draw7kArrow(38, 40)}
    ${drawWing(14.4, 40, 20, 34.4, -WING_CONTROL_DIST, -WING_CONTROL_DIST)}
    ${drawWing(20, 34.4, 25.6, 40, WING_CONTROL_DIST, -WING_CONTROL_DIST)}
    ${drawWing(25.6, 40, 20, 45.6, WING_CONTROL_DIST, WING_CONTROL_DIST)}
    ${drawWing(20, 45.6, 14.4, 40, -WING_CONTROL_DIST, WING_CONTROL_DIST)}
  `;

  return wrapNodeIconInSvgTag(icon, ICON_WIDTH, ICON_HEIGHT, 'topLeft', opts);
}

export const nx7000Icon: NodeTypeIcon = createRectangleNodeTypeIcon(
  ICON_WIDTH,
  ICON_HEIGHT,
  createNx7000Icon,
);
